import get from 'lodash/get';
import reduce from 'lodash/reduce';
import intersection from 'lodash/intersection';
import { flattenProducts } from '@nexus/fff-cart';

import queryString from 'query-string';
import env from 'utils/env';
import { CAMPAIGN_TYPE, getCampaignType, campaignId } from 'utils/campaign';
import { PUBLIC_SHOP, COUNTRY_ISO_CODE } from 'utils/subscriptions';
import request from './request';
import requestJson from './requestJson';
import type { Sort } from './sharedTypes';

export function requestProducts(
  offset,
  selectedCategoryId,
  sort,
  shareUrl,
  loggedIn
) {
  const campaignType = getCampaignType();
  const isPublicShop = !loggedIn && campaignType === CAMPAIGN_TYPE.shop;
  const queryParams = {
    campaignType,
    orderBy: sort,
    // offset could equal 0, which is a falsy value
    ...(offset !== null && { offset, limit: 24 }),
    ...(selectedCategoryId && { categoryId: selectedCategoryId }),
    ...(shareUrl && { extraProductShareUrl: shareUrl }),
    ...(campaignId && { campaignId }),
    ...(isPublicShop && {
      productType: PUBLIC_SHOP,
      countryIsoCode: COUNTRY_ISO_CODE,
    }),
  };
  const version = isPublicShop ? '' : '/1';
  const url = `${env.aceApi}${version}/products?${queryString.stringify(
    queryParams
  )}`;
  const headers = {
    Accept: 'application/vnd.fabfitfun.v3+json',
  };
  return request(url, 'GET', headers);
}

export function requestFavoriteProducts(sort) {
  const type = getCampaignType();
  const queryParams = {
    type,
    orderBy: sort,
    ...(campaignId && { campaignId }),
  };
  const requestUrl = `${
    env.aceApi
  }/1/favorites/products?${queryString.stringify(queryParams)}`;
  return request(requestUrl);
}

export async function getProductsById(productIds, loggedIn) {
  const campaignType = getCampaignType();
  const isPublicShop = !loggedIn && campaignType === CAMPAIGN_TYPE.shop;
  const queryParams = {
    campaignType,
    ...(campaignId && { campaignId }),
    ...(isPublicShop && {
      productType: PUBLIC_SHOP,
      countryIsoCode: COUNTRY_ISO_CODE,
    }),
  };
  const api = isPublicShop
    ? env.publicAceApi // only for Intro Product Shop
    : env.aceApi;
  const requestUrl = `${api}/1/products?${queryString.stringify(queryParams)}`;
  const headers = {
    Accept: 'application/vnd.fabfitfun.v2+json',
    'Content-Type': 'application/json',
  };
  const response = await request(
    requestUrl,
    'POST',
    headers,
    JSON.stringify(productIds)
  );
  const {
    products: productsWithVariants,
    productIds: responseIds,
    totalProductCount,
  } = response;
  const { products, variants } = flattenProducts(productsWithVariants);
  return {
    productIds: responseIds,
    products,
    variants,
    totalProductCount,
  };
}

export async function getRatingsById(productsIds) {
  const numberProductIds = productsIds?.map((id) => Number(id));
  const headers = {
    Accept: '*/*',
    'Content-Type': 'application/json',
  };
  const queryParams = { productIds: numberProductIds };
  const requestUrl = `${env.questionnaireApi}/public/reviews/1/products/get-stats`;
  try {
    const response = await requestJson(
      requestUrl,
      'POST',
      headers,
      JSON.stringify(queryParams)
    );
    return response.data.items[0];
  } catch (err) {
    throw new Error(err);
  }
}

export async function getProductHasReviewed(productId) {
  const headers = {
    Accept: '*/*',
    'Content-Type': 'application/json',
  };
  const requestUrl = `${env.questionnaireApi}/reviews/1/self/has-reviewed/products/${productId}`;

  try {
    const response = await requestJson(requestUrl, 'GET', headers);

    return response.data.items[0];
  } catch (err) {
    throw new Error(err);
  }
}

export async function getRatingGraphDetails(productId) {
  const requestUrl = `${env.questionnaireApi}/public/reviews/1/products/${productId}/detailed-stats`;
  try {
    const response = await request(requestUrl);
    return response.data.items[0];
  } catch (err) {
    throw new Error(err);
  }
}

export async function getProducts(
  offset: number | null,
  selectedCategoryId: string | null,
  sort: Sort,
  shareUrl: string | null,
  loggedIn: boolean
) {
  const productResponse = await requestProducts(
    offset,
    selectedCategoryId,
    sort,
    shareUrl,
    loggedIn
  );
  const {
    products: productsWithVariants,
    productIds,
    totalProductCount,
  } = productResponse.data.items[0];
  const { products, variants } = flattenProducts(productsWithVariants);
  return {
    productIds,
    products,
    variants,
    totalProductCount,
  };
}

export async function getBestSellingProducts(
  selectedCategoryId,
  shareUrl,
  selectedSort,
  productRecAbKey,
  loggedIn
) {
  const campaignType = getCampaignType();
  const isPublicShop = !loggedIn && campaignType === CAMPAIGN_TYPE.shop;
  const queryParams = {
    campaignType,
    offset: 0,
    orderBy: selectedSort,
    limit: 24,
    abTestKey: productRecAbKey,
    ...(campaignId && { campaignId }),
    ...(selectedCategoryId && { categoryId: selectedCategoryId }),
    ...(shareUrl && { extraProductShareUrl: shareUrl }),
    ...(isPublicShop && {
      productType: PUBLIC_SHOP,
      countryIsoCode: COUNTRY_ISO_CODE,
    }),
  };
  const version = isPublicShop ? '' : '/1';
  const url = `${env.aceApi}${version}/products?${queryString.stringify(
    queryParams
  )}`;
  const headers = {
    Accept: 'application/vnd.fabfitfun.v3+json',
  };
  const response = await request(url, 'GET', headers);
  const {
    data: { items },
  } = response;
  const {
    products: productsWithVariants,
    totalProductCount,
    productIds,
    sortedProductIds,
  } = items[0];
  const { products, variants } = flattenProducts(productsWithVariants);
  return {
    productIds,
    sortedProductIds,
    totalProductCount,
    products,
    variants,
  };
}

export async function getFavoriteProducts(sort) {
  const campaignType = getCampaignType();
  const queryParams = {
    campaignType,
    orderBy: sort,
    limit: 24,
    ...(campaignId && { campaignId }),
  };
  const url = `${env.aceApi}/1/favorites/products?${queryString.stringify(
    queryParams
  )}`;
  const headers = {
    Accept: 'application/vnd.fabfitfun.v2+json',
  };
  const response = await request(url, 'GET', headers);
  const {
    data: { items },
  } = response;
  const {
    products: productsWithVariants,
    totalProductCount,
    productIds,
    sortedProductIds,
  } = items[0];
  const { products, variants } = flattenProducts(productsWithVariants);
  return {
    productIds,
    sortedProductIds,
    totalProductCount,
    products,
    variants,
  };
}

function requestSponsoredProducts() {
  const campaignType = getCampaignType();
  const queryParams = {
    campaignType,
    isSponsored: true,
    ...(campaignId && { campaignId }),
  };
  const requestUrl = `${env.aceApi}/1/products?${queryString.stringify(
    queryParams
  )}`;
  const headers = {
    Accept: 'application/vnd.fabfitfun.v3+json',
  };
  return request(requestUrl, 'GET', headers);
}

export async function getSponsoredProducts() {
  const productResponse = await requestSponsoredProducts();
  const {
    products: productsWithVariants,
    productIds,
    totalProductCount,
  } = productResponse.data.items[0];
  const { products, variants } = flattenProducts(productsWithVariants);

  return {
    productIds,
    products,
    variants,
    totalProductCount,
  };
}

export const getProductById = (productId) =>
  request(`${env.aceApi}/1/products/${productId}`);

export const getProductBySlug = (productSlug) =>
  request(`${env.aceApi}/products/slugs/${productSlug}`);

export function getFavoritesData() {
  const type = getCampaignType();
  const queryParams = {
    type,
    ...(campaignId && { campaignId }),
  };
  const requestUrl = `${
    env.aceApi
  }/1/favorites/products/ids?${queryString.stringify(queryParams)}`;
  return request(requestUrl);
}

export function updateFavorite(campaignId, productId, liked) {
  const queryParams = {
    isLike: !!liked,
  };
  const requestUrl = `${
    env.aceApi
  }/1/favorites/campaigns/${campaignId}/products/${productId}/likes?${queryString.stringify(
    queryParams
  )}`;
  return request(requestUrl, 'PUT');
}

export const getProductsByCampaignType = (campaignType, auth) => {
  const type = getCampaignType();
  const headers = { Accept: 'application/vnd.fabfitfun.v3+json' };
  const apiUrl = auth ? `${env.aceApi}/1` : env.aceApi;
  const queryParams = {
    campaignType: campaignType || type,
  };
  const requestUrl = `${apiUrl}/products?${queryString.stringify(queryParams)}`;
  return requestJson(requestUrl, 'GET', headers);
};

export const getProductByCampaignSku = (campaignId, sku) =>
  request(
    `${env.aceApi}/products/campaigns/id/${campaignId}/sku/${sku}`,
    'GET'
  );

export const getProductRecommendations = async (
  productId,
  campaignType,
  limit,
  cartVariantIds
) => {
  const queryParams = {
    campaignType,
    limit,
    abTestKey: 'product_product:cosine:1.0',
  };
  const response = await request(
    `${env.aceApi}/1/products/${productId}/recommended?${queryString.stringify(
      queryParams
    )}`,
    'GET'
  );
  const { products: productsWithVariants, productIds } = get(
    response,
    'data.items[0]',
    {}
  );
  // filter out products in the cart
  const filteredProducts = reduce(
    productsWithVariants,
    (result, product, index) => {
      const { variantIds: productVariantIds } = product;
      const isInCart = intersection(productVariantIds, cartVariantIds).length;
      if (!isInCart) {
        return {
          ...result,
          [index]: product,
        };
      }
      return result;
    },
    {}
  );
  const { products, variants } = flattenProducts(filteredProducts);
  const filteredProductIds = Object.keys(filteredProducts);
  const sortedProductIds = intersection(productIds, filteredProductIds);
  return {
    productIds: sortedProductIds,
    products,
    variants,
  };
};

export const getProductBySku = (sku) =>
  // subscribable products are currently only in the default shop campaign
  request(`${env.aceApi}/products/sku?campaignType=SHOP&sku=${sku}`);

export const getSubscribableProducts = (loggedInUser) => {
  const campaignType = getCampaignType();
  const endpoint = loggedInUser
    ? `${env.aceApi}/1/products/refillable/self`
    : `${env.publicAceApi}/1/products/refillable`;
  const queryParams = {
    // subscribable products are currently only in the default shop campaign
    campaignType,
    ...(!loggedInUser && { countryIsoCode: 'US' }),
  };
  const requestUrl = `${endpoint}?${queryString.stringify(queryParams)}`;
  return request(requestUrl);
};

export async function getFilteredProducts(
  filters = [],
  offset = 0,
  limit = 24,
  orderBy
) {
  const headers = {
    Accept: '*/*',
    'Content-Type': 'application/json',
  };
  const campaignType = getCampaignType();
  const queryParams = {
    campaignType,
    ...(campaignId && { campaignId }),
    ...(orderBy && { orderBy }),
    offset,
    limit,
  };
  const requestUrl = `${
    env.storefrontApi
  }/api/1/faceted/filters?${queryString.stringify(queryParams)}`;
  const body = {
    facetFilters: filters,
  };
  const response = await requestJson(
    requestUrl,
    'POST',
    headers,
    JSON.stringify(body)
  );
  const {
    products: productsWithVariants,
    totalProductCount,
    productIds,
    sortedProductIds,
  } = response;
  const { products, variants } = flattenProducts(productsWithVariants);
  return {
    productIds,
    sortedProductIds,
    totalProductCount,
    products,
    variants,
  };
}
